import { ElementIdWithFlagsSize, ModelIdSize, RowKeySize, QC } from "../dt/schema/dt-schema";
import { DtConstants } from "../dt/schema/DtConstants";
import { base64DecodedLen, base64DecToArr, base64EncArr } from "../dt/encoding/base64";

const QCOL_TO_LOOKUP_NAME = {
	[QC.XParent]: "dbId2xparentId",
	[QC.XRooms]: "dbId2xroomIds",
	[QC.Location]: "dbId2xlocationIds",
	[QC.XHostOfAsset]: "dbId2xhostOfAsset",
	[QC.BimElmOfAsset]: "dbId2xbimElmOfAsset",
	[QC.AssetSplitFromBimElm]: "dbId2assetSplitFromBimElm",
};

// Scratch buffer reused while decoding base64 column values in the worker.
let _decodeBuf = new Uint8Array(RowKeySize * 8);

// Pack a sparse { dbId: xrefId[] } map into a compressed-sparse-row (CSR) pair of
// TypedArrays. Afterwards a dbId's xrefIds are values[offsets[dbId] .. offsets[dbId + 1]]
// (an empty range means that dbId has no refs).
function buildCSR(dbId2xrefIds, numDbIds) {
	// Build offsets as a running total. This MUST walk every dbId in [0, numDbIds) -- not
	// only the ones that have xrefs -- so that offsets[dbId] already holds the cumulative
	// start before we add this row's length (empty dbIds carry the total forward). The
	// final entry ends up holding the grand total (= values.length).
	const offsets = new Uint32Array(numDbIds + 1);
	for (let dbId = 0; dbId < numDbIds; dbId++) {
		const xrefIds = dbId2xrefIds[dbId];
		offsets[dbId + 1] = offsets[dbId] + (xrefIds ? xrefIds.length : 0);
	}

	// Scatter each dbId's xrefIds into its block.
	const values = new Uint32Array(offsets[numDbIds]);
	for (const strDbId in dbId2xrefIds) {
		const dbId = +strDbId; // Coerce to number
		const xrefIds = dbId2xrefIds[dbId];
		const start = offsets[dbId];
		for (let i = 0; i < xrefIds.length; i++) {
			values[start + i] = xrefIds[i];
		}
	}

	return { offsets, values };
}

function getUrnByModelId(modelIds) {
	const numModels = modelIds.length / ModelIdSize;
	const urns = new Array(numModels);
	// mIdx 0 is the reserved null model slot; real model ids start at index 1.
	for (let m = 1; m < numModels; m++) {
		urns[m] = DtConstants.DT_MODEL_URN_PREFIX + ":" +
			base64EncArr(modelIds, m * ModelIdSize, ModelIdSize);
	}
	return urns;
}

// Reconstruct an xrefId's [modelUrn, elementIdB64] tuple from the wire buffers.
function encodeFullId(urnByModelId, mIdxByXrefId, eIdByXrefId, xrefId) {
	const mIdx = mIdxByXrefId[xrefId];
	const urn = urnByModelId[mIdx];
	const elementId = base64EncArr(
		eIdByXrefId, xrefId * ElementIdWithFlagsSize, ElementIdWithFlagsSize);
	return [urn, elementId];
}

// A growable byte buffer that appends fixed-size records (model ids, element ids),
// handing back each record's 1-based index. First slot is reserved as the null slot.
// trimmed() slices it down to an exactly-sized, transferable copy.
class PackedBuffer {
	constructor(recordSize, initialCapacity) {
		this.recordSize = recordSize;
		this.buffer = new Uint8Array(recordSize * initialCapacity);
		this.offset = recordSize; // reserve first slot as the null slot
		this.count = 1;
	}

	// Append one record (recordSize bytes from buf[off..]), returning its index.
	append(buf, off) {
		const recordSize = this.recordSize;
		if (this.buffer.length < this.offset + recordSize) {
			const grown = new Uint8Array(this.buffer.length * 3 / 2);
			grown.set(this.buffer);
			this.buffer = grown;
		}

		this.buffer.set(buf.subarray(off, off + recordSize), this.offset);
		this.offset += recordSize;

		return this.count++;
	}

	// A transferable copy trimmed to the bytes actually written.
	trimmed() {
		return this.buffer.slice(0, this.offset);
	}
}

// Compact, transferable storage for external element references (xrefs) produced
// during object-tree building.
//
// Worker side: accumulate xrefs, flatten() to TypedArray wire buffers, transfer
// zero-copy to the main thread via addTransferBuffers().
//
// Main thread: DtExternalRefsStore.materialize(wire) rebuilds the classic facetInfo maps
// (dbId2xparentId, dbId2xroomIds, dbId2xlocationIds, xrefId2fullId, xrefs) once
// at model load — same shapes consumers used before, without structured-cloning
// those objects across the worker boundary.
export class DtExternalRefsStore {

	constructor() {
		// Build state (not transferred; freed in flatten()).
		this.elmtKey2xrefId = new Map();

		this.mkey2mIdx = new Map();
		this.modelsBuffer = new PackedBuffer(ModelIdSize, 8);
		this.xrefId2mIdx = [0]; // index 0 reserved to match the 1-based xrefIds
		this.elementsBuffer = new PackedBuffer(ElementIdWithFlagsSize, 64);
		this.cols = {};
	}

	//===========================================================================
	// Main thread side: materialize
	//===========================================================================

	// Rebuild the classic main-thread xref maps from transferred wire buffers.
	static materialize({ modelIds, mIdxByXrefId, eIdByXrefId, columns }) {
		const xrefId2fullId = {};
		const xrefs = {};
		const n = mIdxByXrefId.length;
		const urnByModelId = getUrnByModelId(modelIds);

		for (let xrefId = 1; xrefId < n; xrefId++) {
			const fullId = encodeFullId(urnByModelId, mIdxByXrefId, eIdByXrefId, xrefId);
			xrefId2fullId[xrefId] = fullId;
			const [urn, elementId] = fullId;
			let perModel = xrefs[urn];
			if (!perModel) {
				xrefs[urn] = perModel = {};
			}
			perModel[elementId] = xrefId;
		}

		const dbId2Maps = {};

		for (const qCol in columns) {
			const name = QCOL_TO_LOOKUP_NAME[qCol] ?? qCol;
			const map = (dbId2Maps[name] ??= {});

			const csr = columns[qCol];
			if (!csr) {
				continue;
			}

			const { offsets, values } = csr;
			for (let dbId = 0; dbId < offsets.length - 1; dbId++) {
				const start = offsets[dbId];
				const end = offsets[dbId + 1];
				if (start === end) {
					continue;
				}
				// The following split is for backwards compatibility
				// We can remove this while simplifying at the consumers since the original intent was to
				// accelerate cross-thread transfers, which is now irrelevant with zero-copy transfers.
				const count = end - start;
				if (count === 1) {
					map[dbId] = values[start];
				} else {
					const arr = new Array(count);
					for (let i = 0; i < count; i++) {
						arr[i] = values[start + i];
					}
					map[dbId] = arr;
				}
			}
		}

		return { ...dbId2Maps, xrefId2fullId, xrefs };
	}

	//===========================================================================
	// Worker side: build + transfer
	//===========================================================================

	// Record one full-length 40B xref, returning its deduped xrefId
	#addXref(buf, off) {
		const modelB64 = base64EncArr(buf, off, ModelIdSize);
		const elemB64 = base64EncArr(buf, off + ModelIdSize, ElementIdWithFlagsSize);

		// Note: elmtKey is not the same as the B64 encoded xref
		// it's just a unique key for the xref.
		const elmtKey = modelB64 + elemB64;
		let xrefId = this.elmtKey2xrefId.get(elmtKey);
		if (xrefId !== undefined) {
			// Already recorded
			return xrefId;
		}

		// New xref
		let mIdx = this.mkey2mIdx.get(modelB64);
		if (mIdx === undefined) {
			// New model id
			mIdx = this.modelsBuffer.append(buf, off);
			this.mkey2mIdx.set(modelB64, mIdx);
		}
		this.xrefId2mIdx.push(mIdx);

		xrefId = this.elementsBuffer.append(buf, off + ModelIdSize);
		this.elmtKey2xrefId.set(elmtKey, xrefId);

		return xrefId;
	}

	// Decode one or multiple xrefs record the value for the given dbId.
	addColumn(qCol, dbId, encoded) {

		this.cols[qCol] ??= {};

		if (!encoded) {
			return;
		}

		const szIds = base64DecodedLen(encoded.length);
		if (_decodeBuf.length < szIds) {
			_decodeBuf = new Uint8Array(szIds);
		}
		base64DecToArr(encoded, _decodeBuf, 0);

		let ids = null;
		for (let j = 0; j < szIds; j += RowKeySize) {
			const xid = this.#addXref(_decodeBuf, j);
			(ids || (ids = [])).push(xid);
		}
		if (ids) {
			this.cols[qCol][dbId] = ids;
		}
	}

	// Finalize the build into transferable buffers
	flatten(numDbIds) {
		this.mIdxByXrefId = Uint32Array.from(this.xrefId2mIdx);
		this.modelIds = this.modelsBuffer.trimmed();
		this.eIdByXrefId = this.elementsBuffer.trimmed();

		this.columns = {};
		for (const col in this.cols) {
			const dbId2xrefIds = this.cols[col];
			this.columns[col] = buildCSR(dbId2xrefIds, numDbIds);
		}

		this.mkey2mIdx = null;
		this.modelsBuffer = null;
		this.elmtKey2xrefId = null;
		this.xrefId2mIdx = null;
		this.elementsBuffer = null;
		this.cols = null;

		return this;
	}

	// Add buffers to the transfer list
	addTransferBuffers(transferList) {
		transferList.push(this.modelIds.buffer);
		transferList.push(this.mIdxByXrefId.buffer);
		transferList.push(this.eIdByXrefId.buffer);

		// The actual dbId -> xrefs by property column
		for (const col in this.columns) {
			const csr = this.columns[col];
			transferList.push(csr.offsets.buffer);
			transferList.push(csr.values.buffer);
		}
	}
}
